import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppContext} from '../context/AppContext';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {FiChevronLeft,FiCheck,FiCamera,FiAlertCircle,FiLock,FiGlobe,FiUser,FiFlag} from 'react-icons/fi';

export default function EditProfile() {
  const {currentUser,updateProfile,globalOptions}=useAppContext();
  const navigate=useNavigate();
  
  const initialPrivacy=currentUser.privacySettings || {
    showRealName: true,
    showNationality: true,
    showTimezone: true,
    showBio: true,
    showLanguages: true
  };

  const [formData,setFormData]=useState({
    ...currentUser,
    privacySettings: initialPrivacy
  });
  const [error,setError]=useState('');
  const [saving,setSaving]=useState(false);

  const handleSubmit=(e)=> {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    setTimeout(()=> {
      const result=updateProfile(formData);
      if (result.success) {
        navigate(`/profile/${currentUser.id}`);
      } else {
        setError(result.message);
        setSaving(false);
      }
    },500);
  };

  const togglePrivacy=(key)=> {
    setFormData(prev=> ({
      ...prev,
      privacySettings: {
        ...prev.privacySettings,
        [key]: !prev.privacySettings[key]
      }
    }));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 pb-32 sm:pb-8">
      <div className="flex items-center justify-between mb-8">
        <button onClick={()=> navigate(-1)} className="p-2 hover:bg-white dark:hover:bg-dark-surface rounded-xl transition-colors">
          <SafeIcon icon={FiChevronLeft} className="text-xl dark:text-white" />
        </button>
        <h1 className="text-xl font-black text-brand-950 dark:text-white uppercase tracking-tight">Edit Profile</h1>
        <div className="w-10"></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Avatar Section */}
        <div className="bg-white dark:bg-dark-surface rounded-[32px] p-8 shadow-sm border border-gray-100 dark:border-dark-border flex flex-col items-center">
          <div className="relative group">
            <img src={formData.avatar} alt="Avatar" className="w-32 h-32 rounded-[2.5rem] object-cover border-4 border-brand-50 dark:border-dark-card shadow-xl" />
            <button type="button" className="absolute bottom-0 right-0 p-3 bg-brand-600 text-white rounded-2xl shadow-lg hover:scale-110 transition-transform">
              <SafeIcon icon={FiCamera} />
            </button>
          </div>
          <p className="mt-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Tap to change image</p>
        </div>

        {/* Basic Information */}
        <div className="bg-white dark:bg-dark-surface rounded-[32px] p-8 shadow-sm border border-gray-100 dark:border-dark-border space-y-6">
          <h2 className="text-xs font-black text-brand-950 dark:text-white uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <SafeIcon icon={FiUser} className="text-brand-600" /> Basic Information
          </h2>
          
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Nickname (Unique)</label>
            <input type="text" value={formData.nickname} onChange={(e)=> setFormData({...formData,nickname: e.target.value})} className={`w-full px-4 py-4 bg-gray-50 dark:bg-dark-card border ${error ? 'border-red-300' : 'border-gray-100 dark:border-dark-border'} rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:text-white transition-all`} />
            {error && (
              <div className="flex items-center gap-2 text-red-500 mt-1 ml-1">
                <SafeIcon icon={FiAlertCircle} className="text-xs" />
                <span className="text-[10px] font-bold uppercase">{error}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Real Name</label>
            <input type="text" value={formData.name || ''} onChange={(e)=> setFormData({...formData,name: e.target.value})} className="w-full px-4 py-4 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:text-white transition-all" />
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Bio</label>
            <textarea value={formData.bio || ''} onChange={(e)=> setFormData({...formData,bio: e.target.value})} rows={3} placeholder="Tell something about yourself..." className="w-full px-4 py-4 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:text-white transition-all resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Nationality</label>
              <select value={formData.nationality} onChange={(e)=> setFormData({...formData,nationality: e.target.value})} className="w-full px-4 py-4 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none appearance-none dark:text-white" >
                {globalOptions.nationalities.map(n=> <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Timezone</label>
              <select value={formData.timezone} onChange={(e)=> setFormData({...formData,timezone: e.target.value})} className="w-full px-4 py-4 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none appearance-none dark:text-white" >
                {globalOptions.timezones.map(t=> <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Privacy Settings Section */}
        <div className="bg-white dark:bg-dark-surface rounded-[32px] p-8 shadow-sm border border-gray-100 dark:border-dark-border space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-black text-brand-950 dark:text-white uppercase tracking-[0.2em] flex items-center gap-2">
              <SafeIcon icon={FiLock} className="text-brand-600" /> Privacy Controls
            </h2>
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Public Visibility</span>
          </div>

          <PrivacyToggle label="Show Real Name" description="Display your name/surname to other users" icon={FiUser} active={formData.privacySettings.showRealName} onToggle={()=> togglePrivacy('showRealName')} />
          <PrivacyToggle label="Show Nationality" description="Share where you are playing from" icon={FiFlag} active={formData.privacySettings.showNationality} onToggle={()=> togglePrivacy('showNationality')} />
          <PrivacyToggle label="Show Bio" description="Let others read your personal description" icon={FiGlobe} active={formData.privacySettings.showBio} onToggle={()=> togglePrivacy('showBio')} />
          <PrivacyToggle label="Show Languages" description="Display languages you can speak" icon={FiGlobe} active={formData.privacySettings.showLanguages} onToggle={()=> togglePrivacy('showLanguages')} />
        </div>

        <button type="submit" disabled={saving} className="w-full py-5 bg-brand-600 text-white rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-brand-200 dark:shadow-brand-900/20 hover:bg-brand-700 active:scale-95 transition-all flex items-center justify-center gap-2" >
          {saving ? (
            <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : (
            <><SafeIcon icon={FiCheck} className="text-lg" /> Save Profile</>
          )}
        </button>
      </form>
    </div>
  );
}

function PrivacyToggle({label,description,icon,active,onToggle}) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-card rounded-2xl border border-gray-100 dark:border-dark-border group hover:border-brand-200 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${active ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-600' : 'bg-gray-200 dark:bg-dark-surface text-gray-400'}`}>
          <SafeIcon icon={icon} />
        </div>
        <div>
          <p className="text-xs font-black text-brand-950 dark:text-white uppercase tracking-tight">{label}</p>
          <p className="text-[10px] text-gray-400 dark:text-dark-muted font-bold uppercase tracking-tight">{description}</p>
        </div>
      </div>
      <button type="button" onClick={onToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${active ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-700'}`} >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}